/**
 * Health app integration - Apple Health (iOS) & Health Connect (Android)
 * Logs flights as workouts with dose metadata (radiation not a native type)
 */

import { Platform } from 'react-native';

export type HealthSyncStatus = 'unavailable' | 'not_authorized' | 'authorized' | 'unknown';

export interface HealthWorkoutInput {
  route: string;
  doseMicroSv: number;
  durationMinutes: number;
  timestamp: string; // ISO
}

/** Check if Health integration is available on this platform */
export function isHealthAvailable(): boolean {
  return Platform.OS === 'ios' || Platform.OS === 'android';
}

/** Get diagnostic info when HealthKit fails (for debugging) */
export function getHealthKitDiagnostic(): string {
  try {
    const { isHealthDataAvailable } = require('@kingstinct/react-native-healthkit');
    const available = Platform.OS === 'ios' ? isHealthDataAvailable() : 'N/A';
    return `HealthKit available: ${available}`;
  } catch (e) {
    return `diagnostic failed: ${e instanceof Error ? e.message : String(e)}`;
  }
}

/** Initialize and request permissions, returns authorization status */
export async function requestHealthAuthorization(): Promise<HealthSyncStatus> {
  if (Platform.OS === 'web') return 'unavailable';

  try {
    if (Platform.OS === 'ios') {
      const {
        isHealthDataAvailable,
        requestAuthorization,
        WorkoutTypeIdentifier,
      } = require('@kingstinct/react-native-healthkit');

      if (!isHealthDataAvailable()) {
        return 'unavailable'; // Simulator or Health not available
      }

      const granted = await requestAuthorization({
        toShare: [WorkoutTypeIdentifier],
      });
      return granted ? 'authorized' : 'not_authorized';
    }

    if (Platform.OS === 'android') {
      const {
        initialize,
        requestPermission,
      } = require('react-native-health-connect');
      const granted = await initialize();
      if (!granted) return 'unavailable';
      const result = await requestPermission([
        { accessType: 'write', recordType: 'ExerciseSession' },
      ]);
      const hasWrite = result?.some(
        (p: { accessType?: string; recordType?: string }) =>
          p?.recordType === 'ExerciseSession' && p?.accessType === 'write'
      );
      return hasWrite ? 'authorized' : 'not_authorized';
    }
  } catch (err) {
    return 'unknown';
  }
  return 'unavailable';
}

/** Write a flight as a workout to the Health app */
export async function writeFlightToHealth(input: HealthWorkoutInput): Promise<boolean> {
  if (Platform.OS === 'web') return false;

  try {
    const start = new Date(input.timestamp);
    const end = new Date(start.getTime() + input.durationMinutes * 60 * 1000);

    if (Platform.OS === 'ios') {
      const {
        saveWorkoutSample,
        WorkoutActivityType,
      } = require('@kingstinct/react-native-healthkit');

      const doseMicroSv = input.doseMicroSv;
      const doseMilliSv = (doseMicroSv / 1000).toFixed(2);
      
      await saveWorkoutSample(
        WorkoutActivityType.other,
        [], // no quantity samples
        start,
        end,
        undefined, // totals - don't use energyBurned (shows as calories)
        {
          HKMetadataKeyWorkoutBrandName: 'FVrad',
          // Make dose prominent in metadata (visible in workout details)
          dose: `${doseMicroSv} μSv (${doseMilliSv} mSv)`,
          doseMicroSv: String(doseMicroSv),
          doseMilliSv: doseMilliSv,
          route: input.route,
          // Descriptive summary for Health app
          summary: `${input.route} • ${doseMilliSv} mSv cosmic radiation`,
        }
      );
      return true;
    }

    if (Platform.OS === 'android') {
      const {
        insertRecords,
        ExerciseType,
      } = require('react-native-health-connect');
      await insertRecords([
        {
          recordType: 'ExerciseSession',
          startTime: start.toISOString(),
          endTime: end.toISOString(),
          exerciseType: ExerciseType.OTHER_WORKOUT,
          title: `Flight: ${input.route}`,
          notes: `Cosmic dose: ${(input.doseMicroSv / 1000).toFixed(2)} mSv`,
        },
      ]);
      return true;
    }
  } catch {
    return false;
  }
  return false;
}
